import { Fragment } from "react/jsx-runtime";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

import { analytics } from "@/config/analytics";
import { CART_ORIGIN } from "@/constants";
import type {
  PostPurchaseRecommendationsDataModel,
  PostPurchaseSurveyParamsDataModel,
} from "@/domains/surveys/models/types";
import { env } from "@/env";
import { Button, Card, Heading } from "@/ui";
import { buildAddToCartCartUrl } from "@/utils/shopify";

type UpsellProductsCardProps = {
  surveyParams: PostPurchaseSurveyParamsDataModel;
  recommendations: PostPurchaseRecommendationsDataModel;
};

export const UpsellProductsCard = ({ recommendations, surveyParams }: UpsellProductsCardProps) => {
  return (
    <Card className="flex flex-col">
      <div className="flex flex-col gap-6 px-6 py-7 md:gap-8 md:px-14 md:py-12">
        <Heading level={2}>More Products for {surveyParams.symptoms}</Heading>
        <p>They can be used instead of or with your Guided Rapid Relief Inhaler.</p>

        <hr className="border border-b border-line md:hidden" />

        <div className="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-8">
          {recommendations.upsellProducts.map((product, index) => {
            const productPath = new URL(product.url).pathname;

            return (
              <Fragment key={index}>
                {index > 0 && <hr className="border border-b border-line md:hidden" />}
                <div>
                  <div className="flex h-full flex-col gap-6">
                    <div className="flex items-center justify-between gap-3">
                      <Heading level={4}>{product.name}</Heading>
                      <div className="text-xl font-bold text-content-secondary">
                        {product.displayPrice}
                      </div>
                    </div>
                    <img alt={product.name} className="w-full rounded-lg" src={product.imgUrl} />
                    <p className="flex-1">{product.description}</p>
                    <Button variant="secondary-dark" asChild>
                      <a
                        className="flex w-full items-center justify-center"
                        href={buildAddToCartCartUrl({
                          attributes: {
                            origin: CART_ORIGIN.POST_PURCHASE,
                          },
                          variantId: product.variantId,
                          redirectTo: `${productPath}?age_gate=0`,
                        })}
                        onClick={() => {
                          return analytics.track("post_purchase_upsell_product_viewed", {
                            variant_id: product.variantId,
                          });
                        }}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <span className="text-content">Add To Cart</span>
                        <ArrowRightIcon className="h-5 text-content" />
                      </a>
                    </Button>
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
        <Button asChild>
          <a
            className="flex w-full items-center justify-center"
            href={`${env.VITE_SHOPIFY_DOMAIN}/collections/all`}
            onClick={() => {
              return analytics.track("post_purchase_all_products_viewed");
            }}
            rel="noreferrer"
            target="_blank"
          >
            <span>See All Products</span>
            <ArrowRightIcon className="h-5" />
          </a>
        </Button>
      </div>
    </Card>
  );
};
